import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useSystemFeatureVisibility } from '@/lib/system-feature-visibility'
import { useFormBuilderStore } from '@/stores/formBuilderStore'
import type { LogicCondition, LogicJump, Question, QuestionGroupReference } from '@/types/form'
import {
  AlertTriangle,
  ArrowDown,
  ArrowRight,
  ArrowUp,
  Copy,
  FlaskConical,
  GitBranch,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { LogicRuleEditor } from './LogicRuleEditor'
import { LogicSimulationDialog } from './LogicSimulationDialog'
import { VariablePanel } from './VariablePanel'

function conditionValueLabel(condition: LogicCondition, question?: Question) {
  if (condition.operator === 'is_answered') return '已填答'
  if (condition.operator === 'is_not_answered') return '未填答'
  if (condition.operator === 'between' && Array.isArray(condition.value)) {
    return `${condition.value[0] ?? ''} - ${condition.value[1] ?? ''}`
  }
  const choice = question?.choices.find((item) => item.value === condition.value)
  return choice?.label ?? String(condition.value ?? '')
}

function operatorLabel(operator: LogicCondition['operator']) {
  const labels: Record<LogicCondition['operator'], string> = {
    equals: '等於',
    not_equals: '不等於',
    contains: '包含',
    not_contains: '不包含',
    starts_with: '開頭是',
    ends_with: '結尾是',
    greater_than: '大於',
    less_than: '小於',
    greater_or_equal: '大於或等於',
    less_or_equal: '小於或等於',
    is_answered: '已填答',
    is_not_answered: '未填答',
    includes: '包含選項',
    not_includes: '不包含選項',
    between: '介於',
  }
  return labels[operator]
}

function describeCondition(condition: LogicCondition, questions: Question[]) {
  const question = questions.find((item) => item.id === condition.field)
  const title = question?.title ?? condition.field
  if (condition.operator === 'is_answered' || condition.operator === 'is_not_answered') {
    return `「${title}」${operatorLabel(condition.operator)}`
  }
  return `「${title}」${operatorLabel(condition.operator)}「${conditionValueLabel(
    condition,
    question,
  )}」`
}

function groupTitle(groupId: string, questionGroups: QuestionGroupReference[]) {
  return questionGroups.find((group) => group.id === groupId)?.title ?? groupId
}

function describeTarget(
  jump: LogicJump,
  questions: Question[],
  questionGroups: QuestionGroupReference[],
) {
  if (jump.targetGroupId) return `題組：${groupTitle(jump.targetGroupId, questionGroups)}`
  if (jump.targetQuestionId === null) return '結束問卷'
  return questions.find((question) => question.id === jump.targetQuestionId)?.title ?? '未選擇目標'
}

function isQuestion(value: Question | undefined): value is Question {
  return Boolean(value)
}

function createDraftJump(
  question: Question,
  questions: Question[],
  existingCount: number,
): LogicJump {
  const targetQuestion = questions.find((item) => item.orderIndex > question.orderIndex)
  return {
    id: crypto.randomUUID(),
    questionId: question.id,
    conditions: [
      { field: question.id, operator: 'equals', value: question.choices[0]?.value ?? '' },
    ],
    operator: 'AND',
    targetQuestionId: targetQuestion?.id ?? null,
    targetGroupId: null,
    order: existingCount,
  }
}

function getFirstQuestionInGroup(
  groupId: string,
  questionGroups: QuestionGroupReference[],
  questions: Question[],
) {
  const group = questionGroups.find((item) => item.id === groupId)
  const questionById = new Map(questions.map((question) => [question.id, question]))
  const explicitQuestion = group?.questionIds.map((id) => questionById.get(id)).find(isQuestion)
  return (
    explicitQuestion ??
    questions.find((question) => question.groupId === groupId) ??
    group?.questions?.[0]
  )
}

function getJumpWarnings(
  jump: LogicJump,
  questions: Question[],
  questionGroups: QuestionGroupReference[],
) {
  const warnings: string[] = []
  const questionById = new Map(questions.map((question) => [question.id, question]))
  const currentQuestion = questionById.get(jump.questionId)
  const targetQuestion = jump.targetQuestionId ? questionById.get(jump.targetQuestionId) : null
  const targetGroup = jump.targetGroupId
    ? questionGroups.find((group) => group.id === jump.targetGroupId)
    : null
  const targetGroupQuestion = jump.targetGroupId
    ? getFirstQuestionInGroup(jump.targetGroupId, questionGroups, questions)
    : null

  if (jump.targetQuestionId && !targetQuestion) warnings.push('目標題目不存在')
  if (jump.targetGroupId && !targetGroup) warnings.push('目標題組不存在')
  if (jump.targetGroupId && targetGroup && !targetGroupQuestion) {
    warnings.push('目標題組沒有可顯示題目')
  }
  if (jump.conditions.some((condition) => !questionById.has(condition.field))) {
    warnings.push('條件欄位不存在')
  }
  if (
    currentQuestion &&
    targetQuestion &&
    targetQuestion.orderIndex <= currentQuestion.orderIndex
  ) {
    warnings.push('可能造成回跳或循環')
  }
  if (
    currentQuestion &&
    targetGroupQuestion &&
    targetGroupQuestion.orderIndex <= currentQuestion.orderIndex
  ) {
    warnings.push('題組目標可能造成回跳或循環')
  }
  if (
    jump.conditions.some(
      (condition) =>
        !['is_answered', 'is_not_answered'].includes(condition.operator) &&
        String(condition.value ?? '').trim() === '',
    )
  ) {
    warnings.push('有條件尚未填值')
  }

  return warnings
}

function getConflictWarnings(jumps: LogicJump[]) {
  const warnings: string[] = []
  const orderCounts = new Map<number, number>()
  for (const jump of jumps) orderCounts.set(jump.order, (orderCounts.get(jump.order) ?? 0) + 1)
  for (const [order, count] of orderCounts) {
    if (count > 1) warnings.push(`排序 ${order + 1} 有 ${count} 條規則，請確認優先順序`)
  }
  return warnings
}

export function LogicPanel() {
  const questions = useFormBuilderStore((state) => state.questions)
  const questionGroups = useFormBuilderStore((state) => state.questionGroups)
  const logicJumps = useFormBuilderStore((state) => state.logicJumps)
  const selectedQuestionId = useFormBuilderStore((state) => state.selectedQuestionId)
  const addLogicJump = useFormBuilderStore((state) => state.addLogicJump)
  const updateLogicJump = useFormBuilderStore((state) => state.updateLogicJump)
  const deleteLogicJump = useFormBuilderStore((state) => state.deleteLogicJump)
  const reorderLogicJumps = useFormBuilderStore((state) => state.reorderLogicJumps)
  const selectedQuestion = questions.find((question) => question.id === selectedQuestionId)
  const [editingJump, setEditingJump] = useState<LogicJump | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<LogicJump | null>(null)
  const [simulationOpen, setSimulationOpen] = useState(false)
  const featureVisibility = useSystemFeatureVisibility()
  const showVariables = featureVisibility['forms.logicVariables']
  const showCalculations = featureVisibility['forms.logicCalculations']
  const showOutcomes = featureVisibility['forms.logicOutcomeBranches']

  const jumps = useMemo(
    () =>
      logicJumps
        .filter((jump) => jump.questionId === selectedQuestionId)
        .sort((a, b) => a.order - b.order),
    [logicJumps, selectedQuestionId],
  )

  if (!selectedQuestion) {
    return (
      <div className="rounded-md border border-dashed border-slate-300 p-4 text-sm leading-6 text-slate-500">
        請先選擇一個問題再設定條件邏輯。邏輯規則會決定此題送出後前往哪一道題或直接結束問卷。
      </div>
    )
  }

  const activeQuestion = selectedQuestion
  const conflictWarnings = getConflictWarnings(jumps)

  function openCreateDialog() {
    setEditingJump(createDraftJump(activeQuestion, questions, jumps.length))
  }

  function saveJump(jump: LogicJump) {
    const exists = logicJumps.some((item) => item.id === jump.id)
    if (exists) {
      updateLogicJump(jump.id, jump)
    } else {
      addLogicJump(jump)
    }
    setEditingJump(null)
  }

  function duplicateJump(jump: LogicJump) {
    addLogicJump({
      ...structuredClone(jump),
      id: crypto.randomUUID(),
      order: jumps.length,
    })
  }

  function moveJump(jump: LogicJump, direction: -1 | 1) {
    const currentIndex = jumps.findIndex((item) => item.id === jump.id)
    const nextIndex = currentIndex + direction
    if (currentIndex < 0 || nextIndex < 0 || nextIndex >= jumps.length) return
    const orderedIds = jumps.map((item) => item.id)
    const [movedId] = orderedIds.splice(currentIndex, 1)
    orderedIds.splice(nextIndex, 0, movedId)
    reorderLogicJumps(jump.questionId, orderedIds)
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm leading-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 font-medium text-slate-950">
              <GitBranch className="size-4 text-indigo-600" />
              目前問題：{activeQuestion.title}
            </div>
            <p className="mt-1 text-slate-500">
              規則會依排序由上往下判斷，第一個符合的規則生效；若都不符合，填答者會前往下一題。
            </p>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={() => setSimulationOpen(true)}>
            <FlaskConical className="size-4" />
            測試邏輯
          </Button>
        </div>
      </div>

      {conflictWarnings.length > 0 ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <div className="flex items-center gap-2 font-medium">
            <AlertTriangle className="size-4" />
            規則排序需要確認
          </div>
          <ul className="mt-2 list-inside list-disc space-y-1">
            {conflictWarnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {jumps.length === 0 ? (
        <div className="rounded-md border border-dashed border-slate-300 p-4 text-sm leading-6 text-slate-500">
          尚未建立規則。點擊「新增規則」後，可在全螢幕設定介面規劃條件、運算子與跳轉目標。
        </div>
      ) : (
        <div className="space-y-3">
          {jumps.map((jump, index) => (
            <div key={jump.id} className="rounded-lg border border-slate-200 bg-white p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="secondary">規則 {index + 1}</Badge>
                    <Badge className="bg-indigo-50 text-indigo-700 hover:bg-indigo-50">
                      {jump.operator === 'AND' ? '全部符合' : '任一符合'}
                    </Badge>
                  </div>
                  <div className="mt-3 space-y-2 text-sm leading-6 text-slate-600">
                    {jump.conditions.map((condition, conditionIndex) => (
                      <p key={`${jump.id}-${conditionIndex}`} className="line-clamp-2">
                        {describeCondition(condition, questions)}
                      </p>
                    ))}
                  </div>
                  <div className="mt-3 flex items-center gap-2 rounded-md bg-indigo-50 px-3 py-2 text-sm text-indigo-950">
                    <ArrowRight className="size-4 shrink-0" />
                    <span className="min-w-0 truncate">
                      {describeTarget(jump, questions, questionGroups)}
                    </span>
                  </div>
                  {getJumpWarnings(jump, questions, questionGroups).length > 0 ? (
                    <div className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-800">
                      {getJumpWarnings(jump, questions, questionGroups).join('、')}
                    </div>
                  ) : null}
                </div>
                <div className="flex shrink-0 flex-col gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    disabled={index === 0}
                    onClick={() => moveJump(jump, -1)}
                  >
                    <ArrowUp className="size-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    disabled={index === jumps.length - 1}
                    onClick={() => moveJump(jump, 1)}
                  >
                    <ArrowDown className="size-4" />
                  </Button>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setEditingJump(jump)}
                >
                  <Pencil className="size-4" />
                  編輯
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => duplicateJump(jump)}
                >
                  <Copy className="size-4" />
                  複製
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="text-rose-600 hover:text-rose-700"
                  onClick={() => setDeleteTarget(jump)}
                >
                  <Trash2 className="size-4" />
                  刪除
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Button
        type="button"
        variant={jumps.length ? 'outline' : 'default'}
        onClick={openCreateDialog}
      >
        <Plus className="size-4" />
        新增規則
      </Button>

      <VariablePanel
        questions={questions}
        showVariables={showVariables}
        showCalculations={showCalculations}
        showOutcomes={showOutcomes}
      />

      <LogicSimulationDialog
        open={simulationOpen}
        onOpenChange={setSimulationOpen}
        questions={questions}
        questionGroups={questionGroups}
        logicJumps={logicJumps}
        hiddenFields={{}}
        selectedQuestionId={selectedQuestionId}
      />

      {editingJump && (
        <LogicRuleEditor
          open={Boolean(editingJump)}
          onOpenChange={(open) => {
            if (!open) setEditingJump(null)
          }}
          jump={editingJump}
          questions={questions}
          questionGroups={questionGroups}
          currentQuestionId={activeQuestion.id}
          onSave={saveJump}
          onDelete={
            logicJumps.some((jump) => jump.id === editingJump.id)
              ? () => {
                  deleteLogicJump(editingJump.id)
                  setEditingJump(null)
                }
              : undefined
          }
        />
      )}

      <AlertDialog
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>刪除此邏輯規則？</AlertDialogTitle>
            <AlertDialogDescription>
              刪除後，此題將不再依這組條件跳轉。此操作只會影響目前編輯中的問卷狀態。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteTarget) deleteLogicJump(deleteTarget.id)
                setDeleteTarget(null)
                setEditingJump(null)
              }}
            >
              刪除規則
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
